import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * GRI TNT booking cart guard.
 *
 * sections/gri-tnt.liquid books a reveal as several real cart lines: the hire
 * itself, optional upgrades, and -- for a Sydney Full Setup -- a Distance
 * Surcharge ($100 per 10 km past 25 km) and, with no mains power, a $70
 * Generator Hire. They are separate lines because a line property does not
 * change what Shopify charges. Every add-on carries _linked_to.
 *
 * That leaves two holes the customer can walk through in the cart drawer:
 *
 *   1. Delete the surcharge line, keep the $699 setup. We then drive to the
 *      far suburb for free. The surcharge is not an optional add-on -- it is
 *      part of the price of that booking -- so it must not be removable or
 *      editable. Handled in Liquid, not here: snippets/cart-products.liquid
 *      marks the row and snippets/gri-card-styles.liquid hides its controls,
 *      which cannot be defeated by a re-render the way a JS lock can.
 *
 *   2. Delete the hire itself and leave the surcharge (or an upgrade) behind,
 *      billing for an add-on attached to nothing. That is this file's job.
 *
 * The live Dawn theme solved both by wrapping `window.fetch` and running two
 * MutationObservers on every page. This listens to the theme's own
 * `cartLinesUpdate` event instead and matches on the `_linked_to` property the
 * booking already writes, so it needs no knowledge of the drawer's markup and
 * covers upgrades as well as the surcharge.
 */

const SOURCE = 'gri-tnt-cart-guard';
const LINK_PROP = '_linked_to';
const LINK_VALUE = 'TNT Reveal Booking';

/** Any of these in the cart means a booking is still live. */
const PARENT_VARIANTS = new Set([
  44652739657817, 44652739690585, 44652739723353, // Full Setup: Blue, Pink, Secret
  43033108873305, 43033108906073, 43033108840537, // Self-hire: Blue, Pink, Secret
]);

/** A removal re-fires cartLinesUpdate, so bound the work and never re-enter. */
const MAX_PASSES = 3;
let busy = false;
let listening = false;

/**
 * Not the cart-items component itself: it ignores events whose target is
 * itself (it assumes it made the change and already re-rendered), so the
 * drawer never redrew after our removals and only caught up when some other
 * script touched the cart. Same choice as cart-rewards.js.
 * @returns {EventTarget}
 */
function dispatcher() {
  return document.body;
}

function cartSectionIds() {
  return [...document.querySelectorAll('[data-section-id]')]
    .map((el) => el.getAttribute('data-section-id'))
    .filter(Boolean);
}

/**
 * Removes cart lines by key in ONE request, through the theme's event contract,
 * so the drawer, the header count and the cart page re-render once.
 *
 * It used to remove one line per request and wait for each re-render. A full
 * booking carries up to four add-ons (6 Big Cannons, Double Powder, distance
 * surcharge, generator), so deleting the hire took about ten seconds of lines
 * vanishing one by one -- and every extra request was another chance for
 * Shopify's rate limit to cut the sweep short and leave an add-on behind.
 * @param {string[]} lineKeys
 */
async function removeLines(lineKeys) {
  const deferred = CartLinesUpdateEvent.createPromise();

  dispatcher().dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'remove',
      context: 'cart',
      lines: lineKeys.map((id) => ({ id, quantity: 0 })),
      promise: deferred.promise,
    })
  );

  const updates = {};
  for (const key of lineKeys) updates[key] = 0;
  const body = JSON.stringify({
    updates,
    sections: cartSectionIds().join(','),
    sections_url: window.location.pathname,
  });

  try {
    const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));
    const json = JSON.parse(await response.text());
    if (json.errors) throw new Error(JSON.stringify(json.errors));

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(json),
      detail: {
        items: json.items,
        itemCount: json.item_count,
        sections: json.sections,
        source: SOURCE,
        didError: false,
      },
    });
    return json;
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

/**
 * Drops booking add-ons whose parent hire is gone.
 *
 * Reads the cart rather than trusting the event payload: the drawer, the cart
 * page and cart-rewards.js all write too, so the event that woke us may already
 * be stale.
 */
async function sweepOrphans() {
  if (busy) return;
  busy = true;
  try {
    for (let pass = 0; pass < MAX_PASSES; pass++) {
      const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      const items = cart.items ?? [];

      const hasParent = items.some((item) => PARENT_VARIANTS.has(Number(item.id)));
      if (hasParent) return;

      const orphans = items.filter((item) => (item.properties ?? {})[LINK_PROP] === LINK_VALUE);
      if (!orphans.length) return;

      // All at once; the loop only runs again if something re-added a line meanwhile.
      await removeLines(orphans.map((item) => item.key));
    }
  } catch {
    // A failed sweep leaves the cart as the customer left it. Checkout still
    // shows every line, so this never silently changes what they pay.
  } finally {
    busy = false;
  }
}

/** @param {Event} event */
function onCartLinesUpdate(event) {
  // Our own removals re-fire the event; let the loop above drive those.
  if (/** @type {any} */ (event).detail?.data?.source === SOURCE) return;
  sweepOrphans();
}

function startListening() {
  if (listening) return;
  listening = true;
  document.addEventListener(StandardEvents.cartLinesUpdate, onCartLinesUpdate);
}

startListening();
// Covers a cart that was already orphaned before this page loaded.
sweepOrphans();
